/**
 * Sample `n` items without replacement using a partial Fisher-Yates shuffle.
 *
 * `rng` is an injectable [0,1) random source. The revival / random-USE tail
 * of INF PLAY passes a SEEDED rng (mulberry32 keyed on the learner's position)
 * so the steady-state USE stream is deterministic: same learner + same
 * position ⇒ same sequence, every session and every regeneration. This is what
 * makes online INF PLAY navigable (back-nav returns to what was just heard)
 * and matches the offline seeded model. Main-loop callers omit `rng` and get
 * the legacy unseeded `Math.random()` — main play is unchanged.
 */
export function sampleWithoutReplacement<T>(arr: T[], n: number, rng: () => number = Math.random): T[] {
  if (n >= arr.length) return [...arr]
  // Track only the positions touched by the partial shuffle. Copying the
  // whole course pool for each 10–22-item revival draw costs O(pool size).
  const positions = new Map<number, number>()
  const out: T[] = []
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(rng() * (arr.length - i))
    out.push(arr[positions.get(j) ?? j])
    positions.set(j, positions.get(i) ?? i)
    positions.delete(i)
  }
  return out
}
